import {
  useCreateNewUserMutation,
  useGetAllCarsForSaleQuery,
  useCarByIdQuery,
  useGetUserListingsQuery,
  useGetAllMakeModelGenQuery,
  useCreateCarForSaleMutation,
} from "./generated/graphql";
import { useQuery } from '@apollo/client';
import getUserByIdQuery from './graphql/query/getUserById.query';
import getSellerVerificationsQuery from './graphql/query/getSellerVerifications.query';
import getSellerReviewsQuery from './graphql/query/getSellerReviews.query';
import getCatalogMakesModelsQuery from './graphql/query/getCatalogMakesModels.query';

export const useCreateNewUser = useCreateNewUserMutation;
export const useGetAllCarsForSale = useGetAllCarsForSaleQuery;
export const useCarById = useCarByIdQuery;
export const useUserListings = useGetUserListingsQuery;
export const useGetAllMakeModelGen = useGetAllMakeModelGenQuery;
export const useCreateCarForSale = useCreateCarForSaleMutation;

// New hooks for seller verification
export const useGetUserById = (userId: number) => {
  return useQuery(getUserByIdQuery, {
    variables: { userId },
    skip: !userId,
  });
};

export const useGetSellerVerifications = (userId: number) => {
  return useQuery(getSellerVerificationsQuery, {
    variables: { userId },
    skip: !userId,
  });
};

export const useGetSellerReviews = (sellerId: number) => {
  return useQuery(getSellerReviewsQuery, {
    variables: { sellerId },
    skip: !sellerId,
  });
};

/**
 * Distinct make/model pairs from the static vehicle catalog. This replaces the
 * curated `popular_makes` / `car_model` dropdown source once the catalog is
 * imported; the caller is expected to still union in makes/models observed on
 * real listings as a safety net for edge cases.
 */
export const useCatalogMakesModels = () => {
  const { data, loading, error } = useQuery<{
    allVehicleCatalogMakeModels: { nodes: Array<{ make: string; model: string }> };
  }>(getCatalogMakesModelsQuery, { fetchPolicy: "cache-first" });

  const byMake = new Map<string, Set<string>>();
  data?.allVehicleCatalogMakeModels?.nodes?.forEach((n) => {
    if (!n.make) return;
    if (!byMake.has(n.make)) byMake.set(n.make, new Set());
    if (n.model) byMake.get(n.make)!.add(n.model);
  });
  const makes = Array.from(byMake.keys()).sort();
  const modelsFor = (make: string): string[] => {
    if (!make) return [];
    for (const [k, v] of byMake) {
      if (k.toLowerCase() === make.toLowerCase()) return Array.from(v).sort();
    }
    return [];
  };
  return { makes, modelsFor, loading, error };
};
